import { z } from 'zod';

import { isCronScheduleSubmittable } from './cronScheduleSave';

export const scheduleFormSchema = z.object({
  deployment: z
    .object({
      baseId: z.string(),
      environmentId: z.string(),
      transformerName: z.string(),
      environmentName: z.string(),
      version: z.string().optional(),
    })
    .nullable()
    .refine((val) => val !== null, { message: 'Please select a deployment' }),

  scheduleType: z.enum(['daily', 'repeating', 'cron']),

  daily: z.object({
    hour: z.number().min(0).max(23),
    minute: z.number().min(0).max(59),
    timeZone: z.string().min(1, 'Please select a timezone'),
  }),

  repeating: z
    .object({
      frequency: z.number().min(1),
      frequencyUnit: z.enum(['minute', 'hour']),
      startingAt: z
        .object({
          hour: z.number().min(0).max(23),
          minute: z.number().min(0).max(59),
        })
        .nullable(),
      timeZone: z.string(),
    })
    .superRefine((data, ctx) => {
      if (data.startingAt && !data.timeZone.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'Please select a timezone',
          path: ['timeZone'],
        });
      }
    }),

  cron: z
    .object({
      expression: z.string().min(1, 'Please enter a cron expression'),
      explain: z.string(),
      timeZone: z.string().min(1, 'Please select a timezone'),
      isDirty: z.boolean().optional(),
      isValid: z.boolean().optional(),
      canonicalExpression: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (!isCronScheduleSubmittable(data)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Please fix the cron schedule before saving',
          path: ['expression'],
        });
      }
    }),

  templateName: z.string().optional(),
});

/** Raw form values while editing (deployment may be null before validation). */
export type ScheduleFormInput = z.input<typeof scheduleFormSchema>;
/** Validated form values after zod parse (deployment is required). */
export type ScheduleFormOutput = z.output<typeof scheduleFormSchema>;
/** @deprecated Use ScheduleFormOutput for submit handlers, ScheduleFormInput for form state. */
export type ScheduleFormData = ScheduleFormOutput;

export interface RepeatingFormValues {
  frequency: number;
  frequencyUnit: 'minute' | 'hour';
  startingAt: { hour: number; minute: number } | null;
  timeZone: string;
}

/** True when repeating "starting at" can be saved (DST path or legacy path). */
export function isRepeatingStartingAtSubmittable(
  repeating: RepeatingFormValues,
  legacyBrowserLocal: boolean
): boolean {
  if (!repeating.startingAt) {
    return true;
  }

  if (legacyBrowserLocal && !repeating.timeZone.trim()) {
    return true;
  }

  return !!repeating.timeZone.trim();
}

/** Client-side validation for create/edit schedule dialogs (edit may allow legacy repeating). */
export function isScheduleConfigValid(
  scheduleType: ScheduleFormData['scheduleType'],
  daily: ScheduleFormData['daily'],
  repeating: RepeatingFormValues,
  cron: ScheduleFormData['cron'],
  options?: { repeatingLegacyNoTimeZone?: boolean }
): boolean {
  switch (scheduleType) {
    case 'daily':
      return !!daily.timeZone.trim();
    case 'repeating':
      return (
        repeating.frequency > 0 &&
        isRepeatingStartingAtSubmittable(repeating, options?.repeatingLegacyNoTimeZone ?? false)
      );
    case 'cron':
      return isCronScheduleSubmittable(cron) && !!cron.timeZone?.trim();
    default:
      return false;
  }
}
